import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface RepairManual {
  id: string;
  title: string | null;
  brand: string | null;
  models: string[];
  language: string | null;
  market: string | null;
  document_type: string | null;
  file_path: string;
  metadata_status: string;
  original_filename: string | null;
  created_at: string;
  /** "exact": at least one entry in models[] matches (or is contained by) the appliance model.
   *  "fallback": same brand only — closest by model-number prefix similarity. */
  kind?: "exact" | "fallback";
  /** How many leading characters of the appliance model overlap with the closest entry in models[] */
  similarity?: number;
}

/** Build a human-friendly "series" label for a model number, e.g. "G 2180" -> "G 2xxx".
 *  Used to explain fallback selections like "closest G 2xxx manual". */
export function seriesLabelFor(modelNumber?: string | null): string | null {
  if (!modelNumber) return null;
  const trimmed = modelNumber.trim();
  // Match a leading letter run + optional space + first digit, then mask the rest with 'x'.
  const match = trimmed.match(/^([A-Za-z]+)\s*([0-9])([0-9]*)/);
  if (!match) return null;
  const [, letters, firstDigit, restDigits] = match;
  const xs = "x".repeat(Math.max(restDigits.length, 3));
  return `${letters.toUpperCase()} ${firstDigit}${xs}`;
}

const browserLang = (): string => {
  if (typeof navigator === "undefined") return "en";
  return (navigator.language || "en").slice(0, 2).toLowerCase();
};

const browserMarket = (): string => {
  if (typeof navigator === "undefined") return "EU";
  const parts = (navigator.language || "en-EU").split("-");
  return (parts[1] || "EU").toUpperCase();
};

const norm = (s: string) => (s || "").toLowerCase().replace(/\s+/g, "");

/** Length of common leading run of letters/digits between two normalized model strings.
 *  Stops at the first divergent character — captures "same series/era" matches like
 *  "g2180" vs "g2xxx" (3) or "g2180" vs "g1180" (1). */
const sharedPrefix = (a: string, b: string): number => {
  const max = Math.min(a.length, b.length);
  let i = 0;
  for (; i < max; i++) {
    if (a[i] !== b[i]) {
      // Treat 'x' in a manual's model placeholder as a wildcard match
      if (b[i] === "x" || a[i] === "x") continue;
      break;
    }
  }
  return i;
};

export function useMatchingManuals(brand?: string | null, modelNumber?: string | null) {
  const [manuals, setManuals] = useState<RepairManual[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!brand) { setManuals([]); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("repair_manuals")
          .select("id,title,brand,models,language,market,document_type,file_path,metadata_status,original_filename,created_at")
          .ilike("brand", brand)
          .eq("metadata_status", "processed");
        if (error) throw error;

        const all = (data ?? []) as RepairManual[];
        const lang = browserLang();
        const market = browserMarket();
        const localePref = (r: RepairManual) =>
          (r.language === lang ? 2 : r.language === "en" ? 1 : 0) +
          (r.market === market ? 2 : r.market === "EU" ? 1 : 0);

        const m = modelNumber ? norm(modelNumber) : "";

        // 1) Exact matches: any entry in models[] equals/contains/is contained by the appliance model
        const exact: RepairManual[] = [];
        const remainder: RepairManual[] = [];
        if (m) {
          for (const r of all) {
            const hit = r.models?.some(x => {
              const nx = norm(x || "");
              return nx === m || nx.includes(m) || m.includes(nx);
            });
            if (hit) exact.push({ ...r, kind: "exact" });
            else remainder.push(r);
          }
        } else {
          remainder.push(...all);
        }

        // 2) Fallback: same brand, ranked by longest shared prefix between appliance model
        //    and the best model entry on each manual. Always include all brand manuals so
        //    the user has something when no exact match exists.
        const fallback: RepairManual[] = remainder.map(r => {
          let best = 0;
          if (m && r.models?.length) {
            for (const x of r.models) {
              const score = sharedPrefix(m, norm(x || ""));
              if (score > best) best = score;
            }
          }
          return { ...r, kind: "fallback" as const, similarity: best };
        });

        // Sort each bucket by locale preference; fallback also by similarity desc, then recency
        exact.sort((a, b) => localePref(b) - localePref(a));
        fallback.sort((a, b) => {
          const sim = (b.similarity ?? 0) - (a.similarity ?? 0);
          if (sim !== 0) return sim;
          const loc = localePref(b) - localePref(a);
          if (loc !== 0) return loc;
          return (b.created_at || "").localeCompare(a.created_at || "");
        });

        if (!cancelled) setManuals([...exact, ...fallback]);
      } catch (e) {
        console.error("useMatchingManuals error:", e);
        if (!cancelled) setManuals([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [brand, modelNumber]);

  const exactMatches = manuals.filter(m => m.kind === "exact");
  const fallbackMatches = manuals.filter(m => m.kind === "fallback");
  const topMatch = exactMatches[0] ?? fallbackMatches[0] ?? null;

  return { manuals, exactMatches, fallbackMatches, topMatch, loading };
}
